import { i18n } from '../i18n.js';
import { clearFoundryIf, setFoundry } from './foundry-instance.js';
import { Firebase } from '../firebase-loader.js';
import { FoundryInventoryWarm } from './foundry-inventory-warm.js';
import { FoundryLifecycle } from './foundry-lifecycle.js';
import { FoundryTabs } from './foundry-tabs.js';
import { MoonDream } from './moon-dream.js';
import { MoonFoundry } from './foundry.js';
import { Platform } from './platform.js';
import { SoundEffects } from './sound-effects.js';
import { Soundtrack } from './soundtrack.js';
import { GameState } from '../game-state.js';
let FoundrySurface = {};


FoundrySurface = (function () {
    const VIEW_STATE_KEY = 'moonfall_foundry_view_v1';
    const SURFACE_OWNER = 'foundry';
    const FONT_REVEAL_TIMEOUT_MS = 4000;
    /* ONE bounded wait for the whole first view: the themed faces AND the
       opening tab's reads (inventory, balance, price, canvas on Moon Fuse; the
       summoner library on Moon Dream). The same 4 s the font gate always had
       and the invitation dialog's ticket uses: past it the surface is shown
       with whatever is still loading in its own truthful state, because a
       gate that can fail to open has deleted the feature behind it. */
    const FIRST_VIEW_CAP_MS = 4000;
    const THEMED_FONT_FACES = Object.freeze([
        '400 1em "Spectral"',
        '500 1em "Spectral"',
        '600 1em "Cinzel"',
        '700 1em "Cinzel"',
    ]);
    const FONT_SAMPLE = 'Moon Fuse';
    let mounted = null;
    let themedFontWarm = null;
    let themedFontState = 'pending';
    const settledPresentation = Promise.resolve();

    function hasFontLoadingApi() {
        return typeof document.fonts?.load === 'function'
            && typeof document.fonts?.check === 'function';
    }

    function themedFontsAreReady() {
        if (!hasFontLoadingApi()) return true;
        if (themedFontState === 'ready') return true;
        /* The 4s timeout latches 'fallback' when a load() promise hangs — but
           the faces routinely finish activating moments later through other
           consumers, and the latch then pinned Georgia on every later open
           for the whole session. check() is synchronous ground truth, so a
           later open promotes the state the instant the ink is really there. */
        try {
            if (THEMED_FONT_FACES.every((face) => document.fonts.check(face, FONT_SAMPLE))) {
                themedFontState = 'ready';
                return true;
            }
        } catch (_) { /* keep the latched state */ }
        return false;
    }

    function activateThemedFonts() {
        if (!hasFontLoadingApi()) {
            themedFontState = 'ready';
            return Promise.resolve('ready');
        }
        if (!themedFontWarm) {
            themedFontWarm = Promise.all(
                THEMED_FONT_FACES.map((face) => document.fonts.load(face, FONT_SAMPLE)),
            ).then((loadedFaces) => {
                const loaded = loadedFaces.every((faces) => faces?.length > 0)
                    && THEMED_FONT_FACES.every(
                        (face) => document.fonts.check(face, FONT_SAMPLE),
                    );
                themedFontState = loaded ? 'ready' : 'fallback';
                return themedFontState;
            }, () => {
                themedFontState = 'fallback';
                return themedFontState;
            });
        }
        return themedFontWarm;
    }

    function awaitThemedFonts() {
        const warm = activateThemedFonts();
        if (!hasFontLoadingApi() || themedFontsAreReady()) return Promise.resolve('ready');
        return new Promise((resolve) => {
            let settled = false;
            const finish = (state) => {
                if (settled) return;
                settled = true;
                clearTimeout(timer);
                resolve(state === 'ready' ? 'ready' : 'fallback');
            };
            const timer = setTimeout(() => {
                // A provider promise is allowed to remain pending forever. Latch
                // the bounded fallback for later opens so each round trip does
                // not repay the same four-second blank gate. A genuinely late
                // successful activation may still warm a future mount.
                if (themedFontState === 'pending') themedFontState = 'fallback';
                finish('fallback');
            }, FONT_REVEAL_TIMEOUT_MS);
            warm.then(finish, () => finish('fallback'));
        });
    }

    function setHostFontState(host, state) {
        if (!host) return;
        host.classList.toggle('foundry-fonts-fallback', state === 'fallback');
        host.dataset.fontState = state;
        /* Moon Fuse paints several things straight onto <body> — the card hover
           preview, the side hint, the notice stack — so they sit OUTSIDE the
           surface root the fallback rule targets. A timed-out gate therefore
           pinned Georgia on the surface while those popups kept rendering
           Cinzel/Spectral: two typefaces on one screen, which is what reads as
           text "morphing". Mirror the state on <body> so the fallback is
           consistent everywhere the surface owns. */
        document.body?.classList.toggle('foundry-fonts-fallback', state === 'fallback');
    }

    /* THE PREPARING STATE. The surface is mounted in full and laid out, but
       unpainted (`visibility: hidden` on the host, foundry.css), so its
       controllers read and measure against their real geometry. Nothing
       about the lobby behind it changes yet: the press is acknowledged on
       the Foundry tile (lobby.js, the shared click guard) and the body class
       that strips the lobby chrome waits for the reveal. */
    function setHostPreparing(host, preparing) {
        if (!host) return;
        host.classList.toggle('foundry-surface-preparing', preparing);
        if (preparing) host.setAttribute('aria-busy', 'true');
        else host.removeAttribute('aria-busy');
    }

    function gameOwnsTheScreen() {
        return Boolean(document.body?.classList.contains('game-transition-pending')
            || document.body?.classList.contains('game-active'));
    }

    // Preload links fetch bytes; FontFaceSet.load additionally decodes and
    // activates faces hidden inside the template before the player opens it.
    // Swallowing happens inside activateThemedFonts, so this boot warm cannot
    // create an unhandled rejection or block ordinary app initialization.
    void activateThemedFonts();

    function claimLobbySurface() {
        const owner = document.body?.dataset?.lobbySurfaceOwner;
        if (owner && owner !== SURFACE_OWNER) return false;
        if (document.body) document.body.dataset.lobbySurfaceOwner = SURFACE_OWNER;
        return true;
    }

    function releaseLobbySurface() {
        if (document.body?.dataset?.lobbySurfaceOwner === SURFACE_OWNER) {
            delete document.body.dataset.lobbySurfaceOwner;
        }
    }

    function normalizeTab(tab) {
        return tab === 'moon-dream' || tab === 'moon-dream-panel'
            ? 'moon-dream-panel'
            : 'moon-fuse-panel';
    }

    function requireElement(id) {
        const element = document.getElementById(id);
        if (!element) throw new Error(`Missing Foundry surface element: #${id}`);
        return element;
    }

    function buildAccountStorageKey(accountId) {
        // This partition key is needed synchronously during first paint. Four
        // independently seeded 32-bit lanes keep it opaque while making the
        // collision risk negligible for the handful of accounts on a device.
        const hashes = [0x811c9dc5, 0x9e3779b9, 0x85ebca6b, 0xc2b2ae35];
        for (let index = 0; index < accountId.length; index += 1) {
            const code = accountId.charCodeAt(index);
            for (let lane = 0; lane < hashes.length; lane += 1) {
                hashes[lane] ^= code;
                hashes[lane] = Math.imul(hashes[lane], 0x01000193) >>> 0;
            }
        }
        const fingerprint = hashes
            .map((hash) => hash.toString(16).padStart(8, '0'))
            .join('');
        return `account_v2_${fingerprint}`;
    }

    function normalizeAccountUid(value) {
        const uid = String(value || '').trim();
        return uid && uid.length <= 128 ? uid : null;
    }

    function resolveAccountIdentity() {
        const gameUser = GameState?.get?.()?.user;
        const authUserId = Firebase?.getUser?.()?.uid;
        const uid = normalizeAccountUid(authUserId || gameUser?.firebaseUid || gameUser?.id);
        return {
            uid,
            storageKey: uid ? buildAccountStorageKey(uid) : null,
        };
    }

    function readViewStateMap() {
        try {
            const parsed = JSON.parse(localStorage.getItem(VIEW_STATE_KEY) || '{}');
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch (_) {
            return {};
        }
    }

    function readAccountViewState(accountId) {
        if (!accountId) return null;
        const state = readViewStateMap()[accountId];
        return state && typeof state === 'object' && !Array.isArray(state) ? state : null;
    }

    function writeAccountViewState(accountId, state) {
        if (!accountId || !state) return;
        try {
            const stateMap = readViewStateMap();
            stateMap[accountId] = state;
            localStorage.setItem(VIEW_STATE_KEY, JSON.stringify(stateMap));
        } catch (_) {
            // Device view persistence is best-effort; server workspace state remains authoritative.
        }
    }

    function captureViewState(current) {
        writeAccountViewState(current.accountId, {
            activeTab: FoundryLifecycle?.getActiveTab?.() || 'moon-fuse-panel',
            fuse: current.foundry?.captureViewState?.() || {},
            dream: MoonDream?.captureViewState?.() || {},
        });
    }

    function safelyDispose(dispose) {
        try {
            dispose?.();
        } catch (error) {
            console.error('[Foundry] Surface cleanup failed:', error);
        }
    }

    function isolateAppBackground(host) {
        const parent = host.parentElement;
        if (!parent) return [];
        return Array.from(parent.children)
            .filter((element) => element !== host && element.tagName !== 'TEMPLATE')
            .map((element) => {
                const state = {
                    element,
                    inert: element.inert === true,
                    ariaHidden: element.getAttribute('aria-hidden'),
                };
                element.inert = true;
                element.setAttribute('aria-hidden', 'true');
                return state;
            });
    }

    function restoreAppBackground(states = []) {
        states.forEach(({ element, inert, ariaHidden }) => {
            if (!element?.isConnected) return;
            element.inert = inert;
            if (ariaHidden === null) element.removeAttribute('aria-hidden');
            else element.setAttribute('aria-hidden', ariaHidden);
        });
    }

    function focusSurface(root, targetTab) {
        const target = root.querySelector(`[data-target="${targetTab}"]`)
            || root.querySelector('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
            || root;
        if (target === root && !root.hasAttribute('tabindex')) root.tabIndex = -1;
        try {
            target.focus({ preventScroll: true });
        } catch (_) {
            target.focus?.();
        }
    }

    function syncMoonFuseSearchPlacement(root) {
        const search = root?.querySelector('#search-input');
        const inventorySlot = root?.querySelector('.foundry-inventory-search-slot');
        const compactTools = root?.querySelector(
            '[data-foundry-tools-for="moon-fuse-panel"]',
        );
        if (!search || !inventorySlot || !compactTools) return;

        const compact = Platform?.isMobileViewport?.() === true;
        const target = compact ? compactTools : inventorySlot;

        /* MARK WHERE THE SEARCH ACTUALLY WENT, and let the CSS key off that.
           The tab-clipping rules that stop the Moon Dream tab running into this
           field were gated on their own media query — `(max-width: 760px) and
           (orientation: landscape) and (max-height: 560px)` — while THIS
           decision uses `Platform.isMobileViewport()`: portrait ≤768px wide, or
           landscape ≤560px tall, at any width. The two disagree over real
           viewports: every portrait phone, and landscape boxes wider than 760
           such as 932x300 and 852x344, put the search in the header with the
           tabs unconstrained. Reading the placement from the DOM removes the
           possibility of the two conditions drifting apart again. */
        if (root.dataset) {
            root.dataset.foundrySearchPlacement = compact ? 'header' : 'inventory';
        }

        if (search.parentElement === target) return;
        if (compact) target.insertBefore(search, target.firstChild);
        else target.appendChild(search);
    }

    function clearSurfaceUrl() {
        const url = new URL(window.location.href);
        if (url.searchParams.get('surface') !== 'foundry') return;
        url.searchParams.delete('surface');
        url.searchParams.delete('tab');
        window.history.replaceState({}, '', url.toString());
    }

    function close({ restoreFocus = true, persistView = true } = {}) {
        if (!mounted) return false;
        const current = mounted;
        mounted = null;

        if (persistView) captureViewState(current);
        safelyDispose(() => current.authUnsubscribe?.());
        current.authUnsubscribe = null;
        FoundryLifecycle?.setActive?.(false);
        current.events.abort();
        safelyDispose(() => current.foundry?.destroy?.({ persist: true }));
        safelyDispose(() => MoonDream?.dispose?.());
        safelyDispose(() => current.tabs?.dispose?.());

        clearTimeout(current.firstViewCap);
        current.host.replaceChildren();
        current.host.hidden = true;
        current.host.setAttribute('aria-hidden', 'true');
        setHostPreparing(current.host, false);
        current.host.classList.remove('foundry-fonts-fallback');
        document.body.classList.remove('foundry-fonts-fallback');
        current.host.removeAttribute('data-font-state');
        document.body.classList.remove('foundry-surface-active');
        releaseLobbySurface();
        restoreAppBackground(current.backgroundState);
        clearFoundryIf(current.foundry);
        clearSurfaceUrl();
        // A press still waiting on this surface is answered: it will not open.
        current.resolvePresented();

        if (restoreFocus) {
            document.getElementById('open-foundry-btn')?.focus?.();
        }
        return true;
    }

    /**
     * THE REVEAL: everything the lobby needs to learn about the surface, in
     * the one task that shows it — the body class, the settled typeface, the
     * re-fits against the final chrome, the focus move and the inert
     * background. The browser paints the finished Foundry as its first frame.
     */
    function present(current) {
        if (mounted !== current || current.presented) return;
        if (gameOwnsTheScreen()) {
            // A match that started while the Foundry was preparing owns the
            // screen; covering it would hide the game behind a stale surface.
            close({ restoreFocus: false });
            return;
        }
        current.presented = true;
        clearTimeout(current.firstViewCap);
        let fontState = themedFontsAreReady() ? 'ready' : themedFontState;
        if (fontState === 'pending') {
            // The shared cap expired with the faces still loading: the same
            // stable fallback the font timer latches, for this mount and later ones.
            themedFontState = 'fallback';
            fontState = 'fallback';
        }
        const { host, root } = current;
        setHostFontState(host, fontState);
        document.body.classList.add('foundry-surface-active');
        setHostPreparing(host, false);
        /* The tab labels were fitted and the inventory page measured while the
           surface was unpainted; re-fit both against the chrome the body class
           just settled, in this same task, so the first frame is the fitted one. */
        current.tabs?.refit?.();
        safelyDispose(() => current.foundry?.refitToPresentedLayout?.());
        // Move focus before hiding/inerting the lobby that invoked the surface;
        // otherwise browsers reject aria-hidden on the focused ancestor and
        // keyboard users are left on an unreachable control behind the overlay.
        focusSurface(root, current.targetTab);
        current.backgroundState = isolateAppBackground(host);
        current.resolvePresented();
    }

    /** Present when the opening tab's first view and the faces are ready, or at the cap. */
    function presentWhenReady(current, { fontsPending }) {
        const waits = [];
        if (fontsPending) waits.push(awaitThemedFonts());
        const opening = current.targetTab === 'moon-dream-panel' ? MoonDream : current.foundry;
        if (opening?.isFirstViewReady?.() === false) {
            waits.push(Promise.resolve(opening.whenFirstViewReady?.()).catch(() => undefined));
        }
        if (waits.length === 0) {
            present(current);
            return;
        }
        setHostPreparing(current.host, true);
        current.host.dataset.fontState = fontsPending ? 'pending' : themedFontState;
        current.firstViewCap = setTimeout(() => present(current), FIRST_VIEW_CAP_MS);
        void Promise.all(waits).then(() => present(current), () => present(current));
    }

    function open({ tab = null } = {}) {
        if (gameOwnsTheScreen()) return false;
        if (mounted) return mounted.root;
        if (!claimLobbySurface()) return false;

        let host = null;
        let events = null;
        let root = null;
        let foundry = null;
        let tabs = null;
        let current = null;
        try {
            void SoundEffects?.preload?.([
                'forge_reveal',
                'card_reveal',
                'draw',
                'magic',
            ]);
            host = requireElement('foundry-surface-host');
            const initialFontState = themedFontsAreReady() ? 'ready' : themedFontState;
            const fontsPending = initialFontState === 'pending';
            const template = requireElement('foundry-surface-template');
            if (!(template instanceof HTMLTemplateElement)) {
                throw new Error('#foundry-surface-template must be a template element');
            }

            const parentSoundtrack = Soundtrack;
            const parentSoundEffects = SoundEffects;
            const accountIdentity = resolveAccountIdentity();
            const accountId = accountIdentity.storageKey;
            const savedViewState = readAccountViewState(accountId);
            const targetTab = tab === null
                ? normalizeTab(savedViewState?.activeTab)
                : normalizeTab(tab);
            events = new AbortController();

            // Unpainted from the first frame it has a box: the opening tab
            // reads and lays itself out behind this state (presentWhenReady).
            setHostPreparing(host, true);
            host.replaceChildren(template.content.cloneNode(true));
            root = host.querySelector('[data-foundry-surface-root]');
            if (!root) {
                throw new Error('Foundry surface template has no root');
            }

            host.hidden = false;
            host.setAttribute('aria-hidden', 'false');
            FoundryLifecycle?.setActiveTab?.(targetTab);
            FoundryLifecycle?.setActive?.(true);

            root.querySelector('#foundry-lobby-btn')?.addEventListener('click', close, {
                signal: events.signal,
            });

            syncMoonFuseSearchPlacement(root);
            window.addEventListener('moonfall:viewport-change', () => {
                syncMoonFuseSearchPlacement(root);
            }, { signal: events.signal });
            i18n?.applyTranslations?.();
            MoonDream?.mount?.(root);
            MoonDream?.restoreViewState?.(savedViewState?.dream || null);
            foundry = MoonFoundry
                ? new MoonFoundry({
                    root,
                    user: Firebase?.getUser?.() || null,
                    onClose: close,
                    viewState: savedViewState?.fuse || null,
                })
                : null;
            tabs = FoundryTabs?.mount?.(root, {
                initialTab: targetTab,
                onClose: close,
            }) || null;

            let resolvePresented = null;
            const presentation = new Promise((resolve) => { resolvePresented = resolve; });
            current = {
                host,
                root,
                events,
                foundry,
                tabs,
                targetTab,
                accountId,
                accountUid: accountIdentity.uid,
                authUnsubscribe: null,
                parentSoundtrack,
                parentSoundEffects,
                backgroundState: [],
                presented: false,
                firstViewCap: null,
                presentation,
                resolvePresented,
            };
            mounted = current;
            current.authUnsubscribe = Firebase?.subscribe?.((user) => {
                if (mounted !== current) return;
                if (normalizeAccountUid(user?.uid) === current.accountUid) return;
                // A different account must never inherit the previous one's
                // warmed inventory.
                FoundryInventoryWarm?.clear?.();
                // A UID change invalidates every account-bound child controller.
                // Drop the mounted surface without capturing potentially mixed state;
                // the next open restores only the new account's own device view.
                // The restored lobby is now the active surface, so return keyboard
                // focus to its Foundry launcher instead of leaving focus on <body>.
                close({ persistView: false });
            }) || null;
            const captureMountedView = () => {
                if (mounted === current) captureViewState(current);
            };
            window.addEventListener('pagehide', captureMountedView, { signal: events.signal });
            window.addEventListener('beforeunload', captureMountedView, { signal: events.signal });
            setFoundry(foundry);
            /* The tab the player is NOT opening on reads its own first view
               unseen, so switching to it is a swap rather than a load. Moon
               Fuse reads its canvas too, and hands the workspace's lease
               straight back: it belongs to the tab showing it (foundry.js
               peekCanvas). */
            if (targetTab === 'moon-dream-panel') foundry?.prepareFirstView?.();
            else MoonDream?.prepareFirstView?.();
            presentWhenReady(current, { fontsPending });
            return root;
        } catch (error) {
            mounted = null;
            clearTimeout(current?.firstViewCap);
            events?.abort?.();
            safelyDispose(() => foundry?.destroy?.({ persist: false }));
            safelyDispose(() => MoonDream?.dispose?.());
            safelyDispose(() => tabs?.dispose?.());
            FoundryLifecycle?.setActive?.(false);
            restoreAppBackground(current?.backgroundState || []);
            host?.replaceChildren?.();
            if (host) {
                host.hidden = true;
                host.setAttribute('aria-hidden', 'true');
                setHostPreparing(host, false);
                host.classList.remove('foundry-fonts-fallback');
                document.body.classList.remove('foundry-fonts-fallback');
                host.removeAttribute('data-font-state');
            }
            document.body.classList.remove('foundry-surface-active');
            clearFoundryIf(foundry);
            releaseLobbySurface();
            current?.resolvePresented?.();
            throw error;
        }
    }

    return {
        open,
        close,
        isOpen() {
            return mounted !== null;
        },
        getRoot() {
            return mounted?.root || null;
        },
        /** Settles when the open surface has been presented, or closed without it. */
        whenPresented() {
            return mounted?.presentation || settledPresentation;
        },
        whenTypographyReady() {
            return mounted?.presentation || activateThemedFonts();
        },
    };
})();

export { FoundrySurface };
